import decode from "jwt-decode";
import AuthenticationService from "../../../../services/authentication-service";
import { LOGIN, LOGOUT, SET_USER_DATA } from "../types";
import cache from "../../../../hooks/useFetch/defaultCache";
import { hasRole } from "../../../../components/FeatureGate/RoleGate";
import { roles } from "../../../../util/constants";
import { history } from "../../../../store";

const clearStorageKey = (storageArea, key) => {
  try {
    storageArea?.removeItem?.(key);
  } catch (_) {
    // Ignore client-side storage failures.
  }
};

const clearClientSessionData = () => {
  clearStorageKey(localStorage, "user");
  clearStorageKey(localStorage, "chasingInsightsDates");
  clearStorageKey(localStorage, "persist:root");
  clearStorageKey(localStorage, "userActivity");
  clearStorageKey(sessionStorage, "hsEmail");
  clearStorageKey(sessionStorage, "hsToken");
};

const redirectToLoginPage = ({ replace = true, loginState } = {}) => {
  const navigate = replace ? history.replace : history.push;
  const location = loginState
    ? {
        pathname: "/login",
        state: loginState,
      }
    : "/login";

  navigate(location);
};

function getRoles(user) {
  return {
    isAdmin: hasRole(user, [roles.admin]),
    isOwner: hasRole(user, [roles.owner]),
    isManager: hasRole(user, [roles.manager]),
    isViewer: hasRole(user, [roles.receivablesViewer]),
    isContributor: hasRole(user, [roles.receivablesContributor]),
    isBilling: hasRole(user, [roles.billing]),
  };
}

const objUserData = (user) => ({
  ...user,
  firstName: user.profile?.firstName,
  lastName: user.profile?.lastName,
  features: user.profile?.features || {},
  decodedToken: decode(user.token),
  permissions: user.permissions,
  userId: user._id,
  ...getRoles(user),
});

const setUserData = (data) => ({
  type: SET_USER_DATA,
  user: objUserData(data),
});

const setUserLocalStorage = (data) => {
  return localStorage.setItem("user", JSON.stringify(objUserData(data)));
};

const startPendoGuides = (lastLogoutType) => {
  if (lastLogoutType !== "timeout") return;

  try {
    window.pendo?.startGuides?.();
  } catch (_) {
    // Ignore non-critical client-side side-effect failures.
  }
};

const logout =
  (
    logoutType = undefined,
    { redirectToLogin = true, replace = true, loginState } = {},
  ) =>
  async (dispatch, getState) => {
    const token = getState()?.loginReducer?.token;

    if (token) {
      void AuthenticationService.logout().catch(() => true);
    }

    dispatch({ type: LOGOUT, logoutType });

    clearClientSessionData();

    try {
      cache.purge();
    } catch (_) {
      // Ignore non-critical in-memory cache cleanup failures.
    }

    if (redirectToLogin) {
      redirectToLoginPage({ replace, loginState });
    }

    try {
      window.pendo?.stopGuides?.();
    } catch (_) {
      // Ignore non-critical client-side side-effect failures.
    }

    return true;
  };

const login = () => async (dispatch, getState) => {
  const { lastLogoutType } = getState().loginReducer;
  try {
    startPendoGuides(lastLogoutType);
  } finally {
    dispatch({ type: LOGIN });
  }
};

const setUser = (user) => (dispatch, getState) => {
  const { lastLogoutType } = getState().loginReducer;

  dispatch(setUserData(user));
  setUserLocalStorage(user);
  startPendoGuides(lastLogoutType);
};

export { logout, setUser, setUserData, setUserLocalStorage, login };
